// ─── Contexte Espace (Core) ───────────────────────────────────────
// Expose l'espace ACTIF (= tenantId pour les modules), la liste des espaces de
// l'utilisateur, et la création/sélection. L'espace actif est persisté localement.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '../auth/AuthContext';
import {
  ecouterMesEspaces, creerEspace as creerEspaceSvc,
  ecouterEspacesInvites, rejoindreEspace as rejoindreSvc,
  majNomMembre, majEmailMembre,
} from './espaces';

const cleActif = (uid) => `mop:espaceActifId:u:${uid}`;   // actif PAR UTILISATEUR (évite la fuite entre comptes)
const estProprioDe = (e, uid) => !!e && !!uid && (e.creePar === uid || (e.membres || {})[uid] === 'proprietaire');
const EspaceContext = createContext(null);
export const useEspace = () => useContext(EspaceContext)
  || { espaces: [], espaceActif: null, tenantId: null, tenantIds: [], tenantsHerites: [], tenantRangement: null, pret: false };

export function EspaceProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [espaces, setEspaces]   = useState([]);
  const [actifId, setActifId]   = useState(null);
  const [pretActif, setPret]    = useState(false);

  // Restaure l'espace actif mémorisé POUR CE COMPTE. Réinitialise à chaque
  // changement d'utilisateur (bascule rapide) pour ne pas hériter de l'actif
  // du compte précédent.
  useEffect(() => {
    let vivant = true;
    setPret(false);
    setActifId(null);
    if (!uid) { setPret(true); return undefined; }
    (async () => {
      try { const v = await AsyncStorage.getItem(cleActif(uid)); if (vivant && v) setActifId(v); } catch {}
      if (vivant) setPret(true);
    })();
    return () => { vivant = false; };
  }, [uid]);

  const [invites, setInvites] = useState([]);   // espaces où mon e-mail est invité

  const [espacesPrets, setEspacesPrets] = useState(false);   // 1re réponse reçue

  // Mes espaces (temps réel).
  useEffect(() => {
    if (!user?.uid) { setEspaces([]); setEspacesPrets(false); return; }
    setEspacesPrets(false);
    const unsub = ecouterMesEspaces(user.uid, (l) => { setEspaces(l); setEspacesPrets(true); });
    return () => unsub();
  }, [user?.uid]);

  // Invitations en attente (par e-mail) + adhésion automatique : dès qu'on ouvre
  // l'app avec un e-mail invité, on devient membre. C'est le seul moment où l'on
  // peut écrire son propre uid dans l'espace (les règles l'autorisent aux invités).
  useEffect(() => {
    if (!user?.email) { setInvites([]); return; }
    const unsub = ecouterEspacesInvites(user.email, (liste) => {
      const aRejoindre = liste.filter(e => !(e.membreUids || []).includes(user.uid));
      setInvites(aRejoindre);
      aRejoindre.forEach(e => {
        rejoindreSvc(e.id, { uid: user.uid, email: user.email, prenom: user.prenom || '' }).catch(() => {});
      });
    });
    return () => unsub();
  }, [user?.email, user?.uid, user?.prenom]);

  // Espaces que JE POSSÈDE : seuls ceux-là sont « mon espace » (visible/configurable).
  // Un membre n'a pas d'espace actif — il accède aux ÉLÉMENTS (calendriers,
  // inventaires…) qu'un propriétaire partage avec lui, via leurs accès.
  const espacesPossedes = useMemo(() => espaces.filter(e => estProprioDe(e, uid)), [espaces, uid]);

  // Choisit un actif par défaut si aucun/plus valide (parmi les possédés).
  useEffect(() => {
    if (!pretActif) return;
    if (actifId && espacesPossedes.some(e => e.id === actifId)) return;
    setActifId(espacesPossedes[0]?.id || null);
  }, [espacesPossedes, actifId, pretActif]);

  const choisirEspace = useCallback((id) => {
    setActifId(id);
    if (uid) AsyncStorage.setItem(cleActif(uid), id || '').catch(() => {});
  }, [uid]);

  const creerEspace = useCallback(async (nom, emoji) => {
    if (!user?.uid) return null;
    const id = await creerEspaceSvc({ nom, emoji, uid: user.uid, nomCreateur: user.prenom || user.email || '', emailCreateur: user.email || '' });
    choisirEspace(id);
    return id;
  }, [user?.uid, user?.prenom, user?.email, choisirEspace]);

  const espaceActif = useMemo(() => espacesPossedes.find(e => e.id === actifId) || null, [espacesPossedes, actifId]);

  // Auto-réparation du libellé d'affichage : si mon prénom courant diffère de la
  // copie stockée dans l'espace (`noms[uid]`, vide sur les vieux espaces), on la
  // rafraîchit — sinon les membres s'affichent avec un uid tronqué.
  const monNom = user?.prenom || user?.email || '';
  useEffect(() => {
    if (!espaceActif || !user?.uid || !monNom) return;
    const estMembre = (espaceActif.membreUids || []).includes(user.uid);
    const actuel = (espaceActif.noms || {})[user.uid] || '';
    if (estMembre && actuel !== monNom) {
      majNomMembre(espaceActif.id, user.uid, monNom).catch(() => {});
    }
  }, [espaceActif?.id, espaceActif?.noms, user?.uid, monNom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Même auto-réparation pour l'e-mail : les espaces créés avant cet incrément
  // n'ont pas de `emails[uid]` — sans quoi des membres homonymes (même prénom)
  // restent indistinguables dans la liste.
  const monEmail = user?.email || '';
  useEffect(() => {
    if (!espaceActif || !user?.uid || !monEmail) return;
    const estMembre = (espaceActif.membreUids || []).includes(user.uid);
    const actuel = (espaceActif.emails || {})[user.uid] || '';
    if (estMembre && actuel !== monEmail.trim().toLowerCase()) {
      majEmailMembre(espaceActif.id, user.uid, monEmail).catch(() => {});
    }
  }, [espaceActif?.id, espaceActif?.emails, user?.uid, monEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Un element (calendrier, inventaire, plan…) a besoin d'un espace pour
  // exister. Quand l'utilisateur n'en possede aucun, on le cree A SON NOM :
  // c'est le contenant evident, et il pourra le renommer. Sans cela, le ＋
  // repondait dans le vide — le pire des comportements.
  const assurerEspace = useCallback(async () => {
    if (espaceActif?.id) return espaceActif.id;
    if (espacesPossedes[0]?.id) return espacesPossedes[0].id;
    const nom = user?.prenom || user?.email || 'Mon espace';
    const id = await creerEspaceSvc({ nom, emoji: '\u{1F3E0}', uid, nomCreateur: user?.prenom || user?.email || '', emailCreateur: user?.email || '' });
    setActifId(id);
    return id;
  }, [espaceActif, espacesPossedes, uid, user?.prenom, user?.email]);

  const value = useMemo(() => {
    const herites = Array.isArray(espaceActif?.tenantsHerites) ? espaceActif.tenantsHerites : [];
    return {
      // `espaces` = uniquement ceux que je POSSÈDE (un membre ne « voit » pas l'espace).
      espaces: espacesPossedes, espaceActif,
      tenantId: espaceActif?.id || null,
      // Tenants HÉRITÉS : identifiants historiques (calendriers MoPCal) adoptés
      // par cet espace. Les modules LISENT sur l'union tenantId ∪ tenantsHerites.
      tenantsHerites: herites,
      tenantIds: espaceActif ? [espaceActif.id, ...herites] : [],
      // Un seul arbre de rangement peut faire foi : celui du 1er tenant hérité
      // s'il y en a un (pour continuer à partager les emplacements avec MoPCal),
      // sinon celui de l'espace.
      tenantRangement: herites[0] || espaceActif?.id || null,
      choisirEspace, creerEspace, assurerEspace,
      // Données de base prêtes : id actif restauré ET 1re liste d'espaces reçue.
      pret: pretActif && espacesPrets,
    };
  }, [espacesPossedes, espaceActif, choisirEspace, creerEspace, assurerEspace, pretActif, espacesPrets]);

  return <EspaceContext.Provider value={value}>{children}</EspaceContext.Provider>;
}
